import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { 
  getFirestore, 
  collection, 
  getDocs, 
  doc, 
  getDoc, 
  query, 
  where,
  updateDoc,
  setDoc
} from "firebase/firestore";
import { getAuth, onAuthStateChanged } from "firebase/auth";

const AllEducatorScience = () => {
  const navigate = useNavigate();
  const [popupContent, setPopupContent] = useState(null);
  const [visibleTopics, setVisibleTopics] = useState(() => {
    const savedVisibility = localStorage.getItem("visibleTopicsScience");
    return savedVisibility ? JSON.parse(savedVisibility) : Array(27).fill(true);
  });
  
  const [formVisible, setFormVisible] = useState(false);
  const [educatorControlsVisible, setEducatorControlsVisible] = useState(false);
  const [students, setStudents] = useState([]);
  const [user, setUser] = useState(null);
  const [finishedItems, setFinishedItems] = useState({}); // New state for finished items
  const auth = getAuth();
  const db = getFirestore();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        const userDocRef = doc(db, "users", currentUser.uid);
        const userSnap = await getDoc(userDocRef);
        if (userSnap.exists()) {
          setUser({ ...userSnap.data(), uid: currentUser.uid });
        } else {
          console.error("No such user found in Firestore!");
        }
      } else {
        console.error("No user is signed in.");
      }
    });

    return () => unsubscribe();
  }, [auth, db]);

  useEffect(() => {
    if (!user?.uid) return; // Ensure user UID exists before making a request
  
    const fetchEducatorSettings = async () => {
      try {
        const userDocRef = doc(db, "users", user.uid);
        const userSnap = await getDoc(userDocRef);
  
        if (userSnap.exists()) {
          const userData = userSnap.data();
          
          // Ensure visibility settings are retrieved from Firestore
          const storedVisibility = userData.scitools?.map(tool => tool.visible) || Array(27).fill(true);
          setVisibleTopics(storedVisibility);
  
          // Sync with localStorage for persistence across sessions
          localStorage.setItem("visibleTopicsScience", JSON.stringify(storedVisibility));
        } else {
          console.error("Educator settings not found!");
        }
      } catch (error) {
        console.error("Error fetching educator settings:", error);
      }
    };
  
    fetchEducatorSettings();
  }, [user]); // Runs when the user state updates

  useEffect(() => {
    if (!user?.uid) return;

    const fetchFinishedItems = async () => {
      const ref = doc(db, "finishedItems", user.uid);
      const snap = await getDoc(ref);
      if (snap.exists()) {
        setFinishedItems(snap.data());
      }
    };

    fetchFinishedItems();
  }, [user]); // Fetch finishedItems on user load
  

  const handleCheckboxChange = (index) => {
    const updatedVisibleTopics = [...visibleTopics];
    updatedVisibleTopics[index] = !updatedVisibleTopics[index];
    setVisibleTopics(updatedVisibleTopics);
    localStorage.setItem("visibleTopicsScience", JSON.stringify(updatedVisibleTopics));
  };

  const handleSaveChanges = async () => {
    if (!user || !user.uid) {
      console.error("User UID is not defined.");
      return;
    }
  
    if (!topics || !Array.isArray(topics)) {
      console.error("Topics is not defined or not an array.");
      return;
    }
  
    const updatedTools = topics.map((topic, index) => ({
      title: topic.title,
      visible: visibleTopics[index] ?? true,  // Ensure visibleTopics[index] exists
    }));
  
    try {
      const userDocRef = doc(db, "users", user.uid); // Ensure user.uid is defined
      await updateDoc(userDocRef, { scitools: updatedTools });
      alert("Changes saved successfully!");
    } catch (error) {
      console.error("Error saving changes:", error);
    }
  };
  

  const handleOpenPopup = (content) => {
    setPopupContent(content);
  };

  const handleClosePopup = () => {
    setPopupContent(null);
  };

  const handleHomeClick = () => {
    if (user) {
      const role = user.role.toLowerCase();
      if (role.includes("student")) {
        navigate("/dashboard");
      } else if (role.includes("educator")) {
        navigate("/educator/dashboard");
      } else if (role.includes("admin") || role.includes("administrator")) {
        navigate("/admin");
      } else {
        console.error("Unknown user role:", role);
      }
    } else {
      console.error("User is not defined.");
    }
  };

  const handleSubjectClick = (subject) => {
    const path = `/educator/all-all-educator-${subject}`;
    navigate(path);
  };

  const handleRequestClick = () => {
    setFormVisible(true);
  };

  const handleFormClose = () => {
    setFormVisible(false);
  };

  const handleEducatorControlsClick = () => {
    setEducatorControlsVisible(true);
  };

  const handleEducatorControlsClose = () => {
    setEducatorControlsVisible(false);
  };

  const handleStudentProgressClick = async () => {
    if (!user || !user.educatorCode) {
      console.error("No logged-in user or educator code found.");
      return;
    }
  
    try {
      const q = query(
        collection(db, "users"),
        where("role", "==", "administrator"),
        where("educatorCode", "==", user.educatorCode)
      );
  
      const querySnapshot = await getDocs(q);
      const matchingUsers = querySnapshot.docs.map(doc => doc.data());
  
      setPopupContent(
        <div>
          <h2 style={{ color: "black" }}>Student Progress</h2>
          {user && (
            <>
              <h2 style={{ color: "black" }}>{user.sirName} {user.firstName} {user.lastName}'s Class</h2>
              <h3>Educator Code: {user.educatorCode}</h3>
            </>
          )}
          <h2>Student List</h2>
          <div style={styles.studentButtons}>
            {matchingUsers.length === 0 ? (
              <p>No students found with the specified criteria.</p>
            ) : (
              matchingUsers.map((student, index) => (
                <button
                  key={index}
                  style={{
                    ...styles.studentButton,
                    backgroundColor: `hsl(${(index * 40) % 360}, 70%, 80%)`,
                  }}
                  onClick={() => handleStudentClick(student.firstName)}
                >
                  {student.firstName}
                </button>
              ))
            )}
          </div>
        </div>
      );
    } catch (error) {
      console.error("Error fetching students:", error);
      setPopupContent(<p>Error loading students.</p>);
    }
  };

  const handleStudentClick = (firstName) => {
    alert(`It's me ${firstName}!`);
  };

  const handleGameClick = (gameUrl) => {
    navigate(gameUrl);
  };

  const handleCheckboxConfirm = (topicIndex, field) => {
    const topicKey = topics[topicIndex].title;
  
    const confirm = window.confirm(
      `%cAre you sure this item is completed?`,
      "font-size: 16px; color: #fff; background: linear-gradient(to right, #4a90e2, #2980b9); padding: 10px; border-radius: 5px; box-shadow: 0 4px 8px rgba(0, 0, 0, 0.3);"
    );
    if (!confirm) return;
  
    const updated = {
      ...finishedItems,
      [topicKey]: {
        ...(finishedItems[topicKey] || {}),
        [field]: { completed: true, timestamp: new Date().toISOString() },
      },
    };
  
    setFinishedItems(updated);
  
    const ref = doc(db, "finishedItems", user.uid);
    updateDoc(ref, updated).catch(() => {
      setDoc(ref, updated);
    });
  };
  
  // Background image URL (set this to null or empty string to use the default color)
  const backgroundImage = "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/images%2Fbg-texture-light-blue-1.png?alt=media&token=059bb3e7-21f9-477b-ad74-fdfd02a715cc"; // Replace with actual background image URL

  const topics = [
    {
      title: "3 States of Matter",
      subtitle1: "",
      standardNumber: "",
      description: "",
      video: "",
      music: "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/music%2F3statesofmatter2025song.mp3?alt=media&token=4ca634b8-d275-41d6-9e49-36d59970db1b",
      lyrics: ["(Chorus)",
"Matter is everything around you.",
"Matter is made of atoms and molecules.",
"3 states: solids, liquids, gases.",
"3 states: solids, liquids, gases.",

"(Verse)",
"Solids, yes, are hard.",
"Liquids are more like water.",
"Gases are like the atmosphere,",
"That surrounds the earth, our air.",
"Solids, yes, are hard.",
"Liquids are more like water.",
"Gases are like the atmosphere,",
"That surrounds the earth, our air.",
"Those are the 3 states of matter,",
"Those are the 3 states of matter.",
"Solids, liquids, gases.",
"Solids, liquids, gases.",

"(Chorus)",
"Matter is everything around you.",
"Matter is made of atoms and molecules.",
"3 states: solids, liquids, gases.",
"3 states: solids, liquids, gases.",

"© Smart Board Learning, LLC. All rights reserved.",],
      game: "",
      titleImage: "", // Empty string means no image for Force
    },
    {
      title: "Adapt to Survive",
      subtitle1: "",
      standardNumber: "",
      description: "",
      video: "",
      music: "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/music%2Fadapttosurvive2025song.mp3?alt=media&token=118906ed-f2bd-4a02-ae36-c519e8166ed8",
      lyrics: ["(Chorus)",
"With changes in the world, every day,",
"We adapt to survive, adapt to survive.",
"With changes in the world, it’s nature’s way.",
"We adapt to survive, adapt to survive.",
"With changes in the world, every day,",
"We adapt to survive, adapt to survive.",
"Some plants and animals have special traits,",
"To adapt to survive, adapt to survive.",

"(Verse 1)",
"A plant with thorns keeps danger away,",
"While other plants get eaten, every day.",
"Camouflage helps an animal hide,",
"Safe from danger, it stays alive.",
"Spots or stripes can blend right in,",
"Hiding from predators, so they can win.",
"In a changing world, traits play a role,",
"Helping animals reach their goal.",

"(Chorus)",
"With changes in the world, every day,",
"We adapt to survive, adapt to survive.",
"With changes in the world, it’s nature’s way.",
"We adapt to survive, adapt to survive.",
"With changes in the world, every day,",
"We adapt to survive, adapt to survive.",
"Some plants and animals have special traits,",
"To adapt to survive, adapt to survive.",

"(Verse 2)",
"Cause and effect, it’s easy to see,",
"Traits help some live and set them free.",
"Reproducing stronger, they’ll stay alive,",
"Passing on traits, so life will thrive.",
"So watch the world, it’s clear and true,",
"Survival depends on what they do.",
"Traits that help, they carry on,",
"The cycle of life will keep moving strong!",

"(Chorus)",
"With changes in the world, every day,",
"We adapt to survive, adapt to survive.",
"With changes in the world, it’s nature’s way.",
"We adapt to survive, adapt to survive.",
"With changes in the world, every day,",
"We adapt to survive, adapt to survive.",
"Some plants and animals have special traits,",
"To adapt to survive, adapt to survive.",

"© Smart Board Learning, LLC. All rights reserved.",],
      game: "",
      titleImage: "", // Empty string means no image for Force
    },
    {
      title: "Adaptive Characteristics",
      subtitle1: "",
      standardNumber: "",
      description: "",
      video: "",
      music: "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/music%2Fadaptivecharacteristics2025song.mp3?alt=media&token=07c0340b-fe8d-4ae7-8c5b-b4355a19bacd",
      lyrics: ["(chorus)",
"Let’s analyze and describe some adaptive characteristics",
"up in the ecosystem.",
"Let’s analyze and describe some adaptive characteristics",
"up in the ecosystem.",

"(verse)",
"Some animals have webbed feet just like an otter.",
"They can search for food in the water, it makes them swim better.",
"Some animals have sharp claws, like meat eaters,",
"and they use those claws for killing their prey.",
"Some use them for digging for food or shelter, hay.",
"Some animals have large beaks, it helps some animals eat.",
"Some animals have large teeth, it helps the animals eat.",
"Some animals have large beaks, it helps some animals eat.",

"(chorus)",
"Let’s analyze and describe some adaptive characteristics",
"up in the ecosystem.",
"Let’s analyze and describe some adaptive characteristics",
"up in the ecosystem.",

"(verse)",
"Some animals have webbed feet just like an otter.",
"They can search for food in the water, it makes them swim better.",
"Some animals have sharp claws, like meat eaters,",
"and they use those claws for killing their prey.",
"Some use them for digging for food or shelter, hay.",
"Some animals have large beaks, it helps some animals eat.",
"Some animals have large teeth, it helps the animals eat.",
"Some animals have large beaks, it helps some animals eat.",

"(chorus)",
"Let’s analyze and describe some adaptive characteristics",
"up in the ecosystem.",
"Let’s analyze and describe some adaptive characteristics",
"up in the ecosystem.",

"© Smart Board Learning, LLC. All rights reserved.",],
      game: "",
      titleImage: "", // Empty string means no image for Force
    },
    {
      title: "Animal Needs",
      subtitle1: "",
      standardNumber: "",
      description: "",
      video: "",
      music: "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/music%2Fanimalneeds2025song.mp3?alt=media&token=53c6f12a-b401-47a5-b189-e4bcc774dc86",
      lyrics: ["(Chorus)",
"Basic needs of animals.",
"Basic needs of animals.",
"Basic needs of animals.",
"Basic needs of animals.",

"(Verse 1)",
"Basic needs of animals, no need to wonder,",
"For animals to survive, they need food, shelter, and water.",
"Basic needs of animals, no need to wonder,",
"For animals to survive, they need food, shelter, and water.",

"(Chorus)",
"Basic needs of animals.",
"Basic needs of animals.",
"Basic needs of animals.",
"Basic needs of animals.",

"(Verse 2)",
"Some animals need sunlight, like plants on the ground.",
"Others need oxygen, to keep them safe and sound.",
"Every living creature, big or small, it’s true,",
"Depends on their environment, just like me and you.",

"(Chorus)",
"Basic needs of animals.",
"Basic needs of animals.",
"Basic needs of animals.",
"Basic needs of animals.",

"© Smart Board Learning, LLC. All rights reserved.",],
      game: "",
      titleImage: "", // Empty string means no image for Force
    },
    {
      title: "Charged It Up",
      subtitle1: "",
      standardNumber: "",
      description: "",
      video: "",
      music: "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/music%2Fchargeitup2025song.mp3?alt=media&token=10a29cc4-9110-436a-8310-e4e5fa2eb7c8",
      lyrics: ["(Chorus)",
"Electric forces, electric forces.",
"Charge it up, charge it up.",
"Electric forces, electric forces.",
"Charge it up, charge it up.",
"Like charges repel, unlike attract,",
"Electric forces make objects act!",
"Charge it up, charge it up.",

"(Verse 1)",
"Rub a balloon, give it a try,",
"Stick it to the wall, watch it fly!",
"Static builds, the charges appear,",
"Electric forces are crystal clear!",
"Lightning strikes, a zap so bright,",
"Electric discharge in the night.",
"Objects can move with a little rub.",
"Charge it up, charge it up.",

"(Chorus)",
"Electric forces, electric forces.",
"Charge it up, charge it up.",
"Electric forces, electric forces.",
"Charge it up, charge it up.",
"Like charges repel, unlike attract,",
"Electric forces make objects act!",
"Charge it up, charge it up.",

"(Verse 2)",
"Test the charges, here’s what to do,",
"See if they pull or push from you.",
"If they repel, the charges are the same,",
"If they attract, opposite’s the name!",
"Test the charges, here’s what to do,",
"See if they pull or push from you.",
"If they repel, the charges are the same,",
"If they attract, opposite’s the name!",

"(Chorus)",
"Electric forces, electric forces.",
"Charge it up, charge it up.",
"Electric forces, electric forces.",
"Charge it up, charge it up.",
"Like charges repel, unlike attract,",
"Electric forces make objects act!",
"Charge it up, charge it up.",

"© Smart Board Learning, LLC. All rights reserved.",],
      game: "",
      titleImage: "", // Empty string means no image for Force
    },
    {
      title: "Circuits",
      subtitle1: "",
      standardNumber: "",
      description: "",
      video: "",
      music: "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/music%2Fcircuits2025song.mp3?alt=media&token=8a8e26ab-6a16-4a36-9534-eb2d9133af8b",
      lyrics: ["(chorus)",
"A circuit is the path that electricity takes.",
"A circuit is the path that electricity takes.",
"A circuit is the path that electricity takes.",
"A circuit is the path that electricity takes.",

"(verse)",
"Electricity can only flow through a closed circuit.",
"Electricity cannot flow through an open circuit.",
"Series circuits have only one path",
"for electricity to flow, electricity to flow.",
"Parallel circuits have more than one path",
"for electricity to flow, electricity to flow.",

"(chorus)",
"A circuit is the path that electricity takes.",
"A circuit is the path that electricity takes.",
"A circuit is the path that electricity takes.",
"A circuit is the path that electricity takes.",

"© Smart Board Learning, LLC. All rights reserved.",],
      game: "",
      titleImage: "", // Empty string means no image for Force
    },
    {
      title: "Climate Zone",
      subtitle1: "",
      standardNumber: "",
      description: "",
      video: "",
      music: "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/music%2Fclimatezones2025song.mp3?alt=media&token=22cc0cd1-9827-4551-a88d-30221bc875a1",
      lyrics: ["(chorus)",
"Around the tropics, it’s warm.",
"Around the poles, it’s cold.",
"Different climate zones, different climate zones,",
"different climate zones.",

"(verse)",
"Tropical Climate Zone,",
"near the equator’s line.",
"Down by the rainforests,",
"warm and humid all the time.",
"Desert Climate Zone,",
"Will it rain? Not.",
"A lot of cacti grow.",
"It’s very dry and hot.",
"Temperate Climate Zone,",
"Has seasons.",
"plants grow in this zone",
"For that reason.",
"Polar Climate Zone",
"Is near the North and South.",
"Very cold, freezing zone.",
"Wear your coats out.",

"(chorus)",
"Around the tropics, it’s warm.",
"Around the poles, it’s cold.",
"Different climate zones, different climate zones,",
"different climate zones.",

"© Smart Board Learning, LLC. All rights reserved.",],
      game: "",
      titleImage: "", // Empty string means no image for Force
    },
    {
      title: "Compression",
      subtitle1: "",
      standardNumber: "",
      description: "",
      video: "",
      music: "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/music%2Fcompression2025song.mp3?alt=media&token=47ce5aec-5d0b-431a-8893-1094e3815fd5",
      lyrics: ["(chorus)",
"When sound travels through matter,",
"it’s in a compression wave, it’s in a compression wave.",
"When sound travels through matter,",
"it’s in a compression wave, it’s in a compression wave.",

"(verse)",
"Compression waves are longitudinal waves.",
"Compression waves are longitudinal waves.",
"Compression waves travel through solids, liquids, and gases.",
"Compression waves travel through solids, liquids, and gases.",
"Compression waves move backwards and forwards",
"in the same direction the wave is traveling.",
"Compression waves travels through solids.",
"Compression waves travels through liquids.",
"Compression waves travels through gases.",
"Compression waves travels through gases.",

"(chorus)",
"When sound travels through matter,",
"it’s in a compression wave, it’s in a compression wave.",
"When sound travels through matter,",
"it’s in a compression wave, it’s in a compression wave.",

"© Smart Board Learning, LLC. All rights reserved.",],
      game: "",
      titleImage: "", // Empty string means no image for Force
    },
    {
      title: "Digestive System",
      subtitle1: "",
      standardNumber: "",
      description: "",
      video: "",
      music: "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/music%2Fdigestivesystem2025song.mp3?alt=media&token=ffadc11c-40b9-4c09-a1de-571b10f18dfc",
      lyrics: ["(chorus)",
"Come take a ride, come take a ride with me.",
"Come take a ride through the digestive system.",
"Come take a ride, come take a ride with me.",
"Come take a ride through the digestive system.",

"(verse 1)",
"We enter through the mouth, through the mouth, yeah.",
"Digestive process begins in the mouth, yeah.",
"With salivary enzymes and the process of chewing,",
"we are partly broken down, we are partly broken down.",
"All aboard we are going through the esophagus into the stomach,",
"where we take a bath in gastric acid,",
"mixed with chyme those are stomach acids.",

"(chorus)",
"Come take a ride, come take a ride with me.",
"Come take a ride through the digestive system.",
"Come take a ride, come take a ride with me.",
"Come take a ride through the digestive system.",

"(verse 2)",
"Next stop is the small intestines.",
"We are broken down more in the small intestines.",
"Sing along, the first part the duodenum,",
"(d-u-o-d-e-n-u-m), (d-u-o-d-e-n-u-m).",
"Then we enter the jejunum.",
"(j-e-j-u-n-u-m), (j-e-j-u-n-u-m),",
"and the ileum, (i-l-e-u-m), (i-l-e-u-m).",
"Next stop is the large intestines.",
"Here we go, entering the cecum,",
"(c-e-c-u-m), (c-e-c-u-m).",
"Solid waste is stored in the rectum, true.",
"Until we do a number two, eww!",

"© Smart Board Learning, LLC. All rights reserved.",],
      game: "",
      titleImage: "", // Empty string means no image for Force
    },
    {
      title: "Ecosystem Levels",
      subtitle1: "",
      standardNumber: "",
      description: "",
      video: "",
      music: "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/music%2Fecosystemlevels2025song.mp3?alt=media&token=936d0836-5ca7-4b69-bb1c-04b8c39d689b",
      lyrics: ["(chorus)",
"There's levels or organization in every ecosystem.",
"There's levels or organization in every ecosystem.",
"You’ve got the organism, you’ve got the population, got the community, got the ecosystem.",
"You’ve got the organism, you’ve got the population, got the community, got the ecosystem.",

"(verse)",
"Let's start with a single organism and call it level one.",
"When that organism reproduce, and they reproduce, population has begun.",
"Now, organisms interact with different organisms, it's a community.",
"To make it an ecosystem, add the living things with the non-living things.",

"(chorus)",
"There's levels or organization in every ecosystem.",
"There's levels or organization in every ecosystem.",
"You’ve got the organism, you’ve got the population, got the community, got the ecosystem.",
"You’ve got the organism, you’ve got the population, got the community, got the ecosystem.",

"© Smart Board Learning, LLC. All rights reserved.",],
      game: "",
      titleImage: "", // Empty string means no image for Force
    },
    {
      title: "Ecosystems All Around",
      subtitle1: "",
      standardNumber: "",
      description: "",
      video: "",
      music: "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/music%2Fecosystemsallaround2025song.mp3?alt=media&token=a82bc582-d017-4571-8774-bb25bfb95717",
      lyrics: ["(chorus)",
"Ecosystems all around, let’s explore.",
"From forests to oceans, there’s so much more!",
"Biotic and abiotic, working together,",
"supporting life through every weather!",

"(verse 1)",
"In the grasslands, the grass sway,",
"Bison and birds roam all day.",
"Small shrubs, insects, and prairie dogs too,",
"They all need shelter, water, and food!",
"In the ocean, fish swim with ease,",
"Seaweed sways in the salty breeze.",
"Ponds and lakes are home to frogs,",
"With lily pads and swimming logs.",

"(chorus)",
"Ecosystems all around, let’s explore.",
"From forests to oceans, there’s so much more!",
"Biotic and abiotic, working together,",
"supporting life through every weather!",

"(verse 2)",
"Forests are filled with towering trees,",
"Beetles and owls in the leafy seas.",
"Rivers and streams flow fast and free,",
"Supporting fish and life, endlessly.",
"Every ecosystem helps life succeed.",
"From land to water, let’s take a look,",
"Ecosystems are nature’s storybook!",

"(chorus)",
"Ecosystems all around, let’s explore.",
"From forests to oceans, there’s so much more!",
"Biotic and abiotic, working together,",
"supporting life through every weather!",

"© Smart Board Learning, LLC. All rights reserved.",],
      game: "",
      titleImage: "", // Empty string means no image for Force
    },
    {
      title: "Electric Scientist",
      subtitle1: "",
      standardNumber: "",
      description: "",
      video: "",
      music: "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/music%2Felectricscientist2025song.mp3?alt=media&token=93b064b2-dd77-4b77-9671-ae9c59a98aff",
      lyrics: ["(chorus)",
"Let's make it measure up, let's make it measure, yeah.",
"12 inches makes one foot.",
"Yes, we measure up, yeah.",
"Let's make it measure up, let's make it measure, yeah.",
"12 inches makes one foot.",
"Yes, we measure up, yeah.",

"(verse)",
"Look on a ruler, you will see numbers.",
"Each number represents an inch, an inch.",
"Now, each number tells how many inches.",
"You measure from beginning to end, end.",

"(chorus)",
"Let's make it measure up, let's make it measure, yeah.",
"12 inches makes one foot.",
"Yes, we measure up, yeah.",
"Let's make it measure up, let's make it measure, yeah.",
"12 inches makes one foot.",
"Yes, we measure up, yeah.",

"(verse)",
"Look on a ruler, you will see notches.",
"Each notch is a different size, size.",
"The notch in the middle is one half.",
"The next notch down a size is one fourth.",
"The next notch down a size is one eight.",
"and the smallest notch is one sixteenth.",

"(chorus)",
"Let's make it measure up, let's make it measure, yeah.",
"12 inches makes one foot.",
"Yes, we measure up, yeah.",
"Let's make it measure up, let's make it measure, yeah.",
"12 inches makes one foot.",
"Yes, we measure up, yeah.",

"© Smart Board Learning, LLC. All rights reserved.",],
      game: "",
      titleImage: "", // Empty string means no image for Force
    },
    {
      title: "Environmental Change",
      subtitle1: "",
      standardNumber: "",
      description: "",
      video: "",
      music: "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/music%2Fenvironmentalchange2025song.mp3?alt=media&token=7fb064e4-0513-4dea-b929-47ff30f1e3e0",
      lyrics: ["(Chorus)",
"Organisms can thrive in changing environments,",
"If they adapt or move away, they still might stand a chance.",
"Organisms can thrive in changing environments,",
"But some may fail to live if they can't meet new demands.",

"(Verse 1)",
"Some organisms adapt to their surroundings' shift,",
"They change their behaviors or grow a special gift.",
"Adaptation.",
"But if the change is sudden and the shift is too extreme,",
"Some might not survive or fulfill their living dreams.",
"Extinction.",
"Some migrate to new spaces where conditions suit them more,",
"While others stay and perish where they were before.",
"Adaptation.",

"(Bridge)",
"If the food supply disappears or the water runs dry,",
"Life can become a struggle, and many might die.",
"But some species are resilient and can find a new way,",
"To make a home in places where others couldn’t stay.",

"(Chorus)",
"Organisms can thrive in changing environments,",
"If they adapt or move away, they still might stand a chance.",
"Organisms can thrive in changing environments,",
"But some may fail to live if they can't meet new demands.",

"(Verse 2)",
"When climates shift to heat waves or freezing snow and ice,",
"Some species can evolve, but others pay the price.",
"Natural Selection.",
"If pollution fills their waters or habitats are gone,",
"It creates new challenges to simply carry on.",
"Survival.",
"Genetics play a role in how species cope with change,",
"Some will grow stronger while others fade away.",
"Natural Selection.",

"(Outro)",
"Organisms rise or fall depending on their fate,",
"How they respond to changes can truly resonate.",
"Adapting or migrating, or even disappearing,",
"The story of survival is both harsh and endearing.",

"(Chorus)",
"Organisms can thrive in changing environments,",
"If they adapt or move away, they still might stand a chance.",
"Organisms can thrive in changing environments,",
"But some may fail to live if they can't meet new demands.",

"© Smart Board Learning, LLC. All rights reserved.",],
      game: "",
      titleImage: "", // Empty string means no image for Force
    },
    {
      title: "Food Web",
      subtitle1: "",
      standardNumber: "",
      description: "",
      video: "",
      music: "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/music%2Ffoodweb2025song.mp3?alt=media&token=d9108a4b-545b-4ce0-ac4f-f96ce831ccd2",
      lyrics: ["(Chorus)",
"In a food web, we see how plants and animals connect.",
"In a food web, we see how plants and animals connect,",
"Through many paths, through many paths.",
"A food chain shows just one way to go,",
"But a food web shows how life can grow.",

"(Verse 1)",
"Trees make acorns, food for bugs and mice.",
"When the mice are here, snakes and owls think it’s nice.",
"Bugs are food for birds, and skunks eat them too.",
"Every animal matters, they all have jobs to do.",
"If one animal goes, the others feel the loss.",
"Like a broken web, it comes at a big cost.",

"(Chorus)",
"In a food web, we see how plants and animals connect.",
"In a food web, we see how plants and animals connect,",
"Through many paths, through many paths.",
"A food chain shows just one way to go,",
"But a food web shows how life can grow.",

"(Verse 2)",
"Plants give food to animals, animals help them grow.",
"If one part disappears, it’s hard for others, you know.",
"Foxes eat the rabbits, rabbits eat the grass.",
"Everything works together so nature can last.",
"The food web is a circle, a balance we must keep.",
"Protecting every animal and the places where they sleep.",

"(Chorus)",
"In a food web, we see how plants and animals connect.",
"In a food web, we see how plants and animals connect,",
"Through many paths, through many paths.",
"A food chain shows just one way to go,",
"But a food web shows how life can grow.",

"(Outro)",
"So let’s take care of nature, the plants, the animals too.",
"The food web keeps them living, and it helps us all too!",

"© Smart Board Learning, LLC. All rights reserved.",],
      game: "",
      titleImage: "", // Empty string means no image for Force
    },
    {
      title: "Forms of Energy",
      subtitle1: "",
      standardNumber: "",
      description: "",
      video: "",
      music: "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/music%2Fformsofenergy2025song.mp3?alt=media&token=5101cfce-2f00-468b-b7c4-89b48eec32cf",
      lyrics: ["(chorus)",
"Different forms of energy.",
"Potential and kinetic,", 
"potential and kinetic.", 
"Different forms of energy.",
"Potential and kinetic,", 
"potential and kinetic.",

"(verse 1)",
"Chemical energy is stored in gas and coal.", 
"Stored in the bonds of atoms and molecules.", 
"Mechanical energy is stored in things",
"that have tension, like rubber bands and springs.", 
"Nuclear energy is stored in the nucleus of an atom,", 
"it can be released in large amounts.",
"Electrical energy is stored in a battery",
"can start my car or power up my T.V.",

"(chorus)",
"Different forms of energy.",
"Potential and kinetic,", 
"potential and kinetic.", 
"Different forms of energy.",
"Potential and kinetic,", 
"potential and kinetic.", 

"(verse 2)",
"Electromagnetic energy is radiant energy,", 
"light, x-rays, radio waves.",
"Motion energy is stored in movement.",
"You get more energy the faster it's moving.", 
"Sound energy is energy through waves,", 
"made when a force cause something to vibrate.",

"(chorus)",
"Different forms of energy.",
"Potential and kinetic,", 
"potential and kinetic.", 
"Different forms of energy.",
"Potential and kinetic,", 
"potential and kinetic.",

"© Smart Board Learning, LLC. All rights reserved.",],
      game: "",
      titleImage: "", // Empty string means no image for Force
    },
    {
      title: "Founders",
      subtitle1: "",
      standardNumber: "",
      description: "",
      video: "",
      music: "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/music%2Ffounders2025song.mp3?alt=media&token=6bc11234-83e1-4818-988c-43535c6396d2",
      lyrics: ["(chorus)",
"Can you name some founders of the United States?,",
"Yes, yes.,",
"Can you name some founders of the United States?,",
"Yes, yes.,",

"(verse)",
"George Washington is called the father of our country.,",
"He was the 1st President of the United States.,",
"Thomas Jefferson wrote the Virginia Statue for Religious Freedom,,",
"for Religious Freedom.,",
"Thomas Jefferson was one of the founders of the",
"Declaration of Independence.,",
"Abraham Lincoln was the 16th president,,",
"he helped to end slavery.,",
"Abraham Lincoln was the 16th president,,",
"he helped to end slavery.,",

"(chorus)",
"Can you name some founders of the United States?,",
"Yes, yes.,",
"Can you name some founders of the United States?,",
"Yes, yes.,",

"© Smart Board Learning, LLC. All rights reserved.",],
      game: "",
      titleImage: "", // Empty string means no image for Force
    },
    {
      title: "Friction",
      subtitle1: "",
      standardNumber: "",
      description: "",
      video: "",
      music: "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/music%2Ffriction2025song.mp3?alt=media&token=38d656a0-f811-4c14-98f3-c7f744ccaa75",
      lyrics: ["(chorus)",
"The resistance to motion is friction.",
"The resistance to motion is friction.",

"(verse)",
"Made by two objects moving against each other, each other.",
"Made by two objects moving against each other, each other.",
"Friction creates heat.",
"Friction creates heat.",
"When you rub your hands together",
"you cause a resistance to motion,",
"then you feel heat from friction, from friction.",

"(chorus)",
"The resistance to motion is friction.",
"The resistance to motion is friction.",

"© Smart Board Learning, LLC. All rights reserved.",],
      game: "",
      titleImage: "", // Empty string means no image for Force
    },
    {
      title: "Fronts",
      subtitle1: "",
      standardNumber: "",
      description: "",
      video: "",
      music: "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/music%2Ffronts2025song.mp3?alt=media&token=11290937-9ece-4375-ac61-c7d6251f7bb0",
      lyrics: ["(chorus)",
"When the air is heated unequally,",
"you get air masses with different densities.",
"When the air is heated unequally,",
"you get air masses with different densities.",
"When the air is heated unequally,",
"you get air masses with different densities.",
"When the air is heated unequally,",
"you get air masses with different densities.",

"(verse)",
"The boundary between air masses,",
"with different temperatures is called a front.",
"The boundary between air masses,",
"with different humidity’s is called a front.",
"A cold front is when cold air",
"pushes underneath a mass of warm air.",
"A warm front is when warm air",
"pushes over a mass of cold air.",
"The boundary between air masses,",
"with different temperatures is called a front.",
"The boundary between air masses,",
"with different humidity’s is called a front.",
"A cold front is when cold air",
"pushes underneath a mass of warm air.",
"A warm front is when warm air",
"pushes over a mass of cold air.",

"(chorus)",
"When the air is heated unequally,",
"you get air masses with different densities.",
"When the air is heated unequally,",
"you get air masses with different densities.",
"When the air is heated unequally,",
"you get air masses with different densities.",
"When the air is heated unequally,",
"you get air masses with different densities.",

"© Smart Board Learning, LLC. All rights reserved.",],
      game: "",
      titleImage: "", // Empty string means no image for Force
    },
    {
      title: "Hurricanes & Tornadoes",
      subtitle1: "",
      standardNumber: "",
      description: "",
      video: "",
      music: "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/music%2Fhurricanesandtornadoes2025song.mp3?alt=media&token=d9ce0886-c701-44bd-baf1-e9e256c0cdac",
      lyrics: ["(chorus)",
"Stormy skies, stormy skies,",
"Spinning winds and raining highs!",
"Stormy skies, stormy skies,",
"Watch the weather dance and rise!",

"(verse 1)",
"Hurricanes form where the ocean's warm,",
"Air rises up in a swirling storm.",
"Winds get strong and the rain pours down,",
"Spinning and spinning all around!",

"(chorus)",
"Stormy skies, stormy skies,",
"Spinning winds and raining highs!",
"Stormy skies, stormy skies,",
"Watch the weather dance and rise!",

"(verse 2)",
"Tornadoes twist when hot meets cold,",
"Spinning fast, brave and bold!",
"Thunderstorms boom, lightning strikes,",
"Tornadoes dance through day and night!",

"(chorus)",
"Stormy skies, stormy skies,",
"Spinning winds and raining highs!",
"Stormy skies, stormy skies,",
"Watch the weather dance and rise!",

"(bridge)",
"Hurricanes have an eye that's calm,",
"But outside winds bring the stormy alarm.",
"Tornadoes are ranked from E-F-0 to E-F-5,",
"The higher the number, the faster they drive.",
"Warm ocean water gives hurricanes their power,",
"They can last for days — not just an hour!",

"(chorus)",
"Stormy skies, stormy skies,",
"Spinning winds and raining highs!",
"Stormy skies, stormy skies,",
"Stormy skies up in the skies!",

"© Smart Board Learning, LLC. All rights reserved.",],
      game: "",
      titleImage: "", // Empty string means no image for Force
    },
    {
      title: "Melting, Boiling, Freezing Points",
      subtitle1: "",
      standardNumber: "",
      description: "",
      video: "",
      music: "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/music%2Fmeltingboilingfreezing2025song.mp3?alt=media&token=3bb7cd69-462c-4be6-a8ec-a213b28bde31",
      lyrics: ["(verse)",
"Melting point is the temperature where a substance melt.",
"Melting point is the temperature where a substance melt.",
"At the melting point, ice can turn from a solid into a liquid.",
"At the melting point, ice melts at 0 degrees Celsius.",
"Freezing point is also the point where water freezes.",
"Freezing point is almost the same as the melting point by degrees.",
"At the freezing point water can turn from a liquid into a solid.",
"At the freezing point, water freezes at 0 degrees Celsius.",
"Boiling point is the temperature where a substance evaporates.",
"Boiling point is the temperature where a substance evaporates.",
"At the boiling point water can turn from a liquid into a gas.",
"At the boiling point, water boils at 100 degrees Celsius.",
"Different substances have different melting point temperatures.",
"Different substances have different boiling point temperatures.",
"Different substances have different freezing point temperatures.",

"© Smart Board Learning, LLC. All rights reserved.",],
      game: "",
      titleImage: "", // Empty string means no image for Force
    },
    {
      title: "Niche",
      subtitle1: "",
      standardNumber: "",
      description: "",
      video: "",
      music: "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/music%2Fniche2025song.mp3?alt=media&token=2486f25c-f7c9-4419-9457-238a497c90ac",
      lyrics: ["(Chorus)",
"During an animal's life cycle, its niche can change,",
"During an animal's life cycle, its niche can change.",

"(Verse 1)",
"A niche is the role an animal plays in its ecosystem,",
"It’s where it finds its food and how it avoids the system.",
"It’s how it helps the environment, and how it survives,",
"Its niche is what keeps the ecosystem alive!",
"A tadpole eats plants, but a frog eats flies,",
"As it grows, its niche changes right before your eyes!",
"A caterpillar munches leaves, a butterfly drinks sweet nectar,",
"Different roles in nature, one creature’s life adventure!",

"(Chorus)",
"During an animal's life cycle, its niche can change,",
"During an animal's life cycle, its niche can change.",

"(Verse 2)",
"No two organisms can share the same niche, it’s true,",
"Each one has its own job that it’s meant to do.",
"From cleaning up the forest to pollinating flowers,",
"Every creature’s niche shows nature’s powers!",
"So remember, every animal has a special role,",
"Its niche helps the ecosystem stay whole.",
"From young to old, as they grow and rearrange,",
"During an animal’s life cycle, its niche can change!",

"(Chorus)",
"During an animal's life cycle, its niche can change,",
"During an animal's life cycle, its niche can change.",

"© Smart Board Learning, LLC. All rights reserved.",],
      game: "",
      titleImage: "", // Empty string means no image for Force
    },
    {
      title: "Parallel Circuits",
      subtitle1: "",
      standardNumber: "",
      description: "",
      video: "",
      music: "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/music%2Fparallelcircuits2025song.mp3?alt=media&token=9a7d3bd6-bee5-410a-9a82-91bfa243e1c0",
      lyrics: ["(chorus)",
"Parallel circuits have more than one resistor.",
"Parallel circuits have more than one resistor.",
"Parallel circuits have more than one resistor.",
"Parallel circuits have more than one resistor.",

"(verse)",
"A parallel circuit is a circuit ",
"with multiple parallel paths.",
"Charges can flow through any of the paths ",
"to keep the flow going.",
"If one of the paths stop because its broken,",
"the other paths will keep on flowing.",

"(chorus)",
"Parallel circuits have more than one resistor.",
"Parallel circuits have more than one resistor.",
"Parallel circuits have more than one resistor.",
"Parallel circuits have more than one resistor.",

"© Smart Board Learning, LLC. All rights reserved.",],
      game: "",
      titleImage: "", // Empty string means no image for Force
    },
    {
      title: "Plants and Animals Need Their Space",
      subtitle1: "",
      standardNumber: "",
      description: "",
      video: "",
      music: "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/music%2Fplantsandanimalsneedtheirspace2025song.mp3?alt=media&token=006814cd-57d6-4312-b230-a84afa79a47f",
      lyrics: ["(Chorus)",
"Plants and animals need their space,",
"Water, food, and a safe, good place.",
"Hot or cold, it must feel right,",
"So, they can grow and live their life!",

"(Verse 1)",
"Animals need water, food, and air,",
"Shelter from rocks or trees they share.",
"Plants need sunlight, space to grow,",
"And bees to help their seeds to sow!",

"(Chorus)",
"Plants and animals need their space,",
"Water, food, and a safe, good place.",
"Hot or cold, it must feel right,",
"So, they can grow and live their life!",

"(Verse 2)",
"A small change can mean a lot,",
"Too little water, or too hot.",
"The parts of nature work as one,",
"Helping life get what’s to be done!",

"(Outro)",
"Living and nonliving work together,",
"For plants and animals, now and forever.",
"An ecosystem is a home, it’s true,",
"Taking care of it is up to me and you!",

"© Smart Board Learning, LLC. All rights reserved.",],
      game: "",
      titleImage: "", // Empty string means no image for Force
    },
    {
      title: "Predator & Prey",
      subtitle1: "",
      standardNumber: "",
      description: "",
      video: "",
      music: "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/music%2Fpredatorandprey2025song.mp3?alt=media&token=95fbe351-6ee2-4148-826a-263a4f10498e",
      lyrics: ["(chorus)",
"Predator or the prey, or the prey, or the prey",
"Predator or the prey, or the prey, or the prey",

"(verse)",
"When an animal hunts another animal to get its food,",
"it’s called the predator.",
"When an animal hunts another animal to get its food,",
"it’s called the predator.",
"When an animal is hunted by another animal for food,",
"it’s called the prey.",
"When an animal is hunted by another animal for food,",
"it’s called the prey.",

"(chorus)",
"Predator or the prey, or the prey, or the prey",
"Predator or the prey, or the prey, or the prey",

"(verse)",
"A lion hunts the zebra, the lion is the predator.",
"The zebra runs fast, it’s the prey, it’s the prey.",
"An owl swoops down on a little mouse,",
"The owl is the predator, outside your house!",
"A fox chase a  rabbit through the grass,",
"The Rabbit is the prey, trying to dash!",
"A hawk sees a snake slither on the ground,",
"Predator and prey, nature’s all around!",

"(chorus)",
"Predator or the prey, or the prey, or the prey",
"Predator or the prey, or the prey, or the prey",

"© Smart Board Learning, LLC. All rights reserved.",],
      game: "",
      titleImage: "", // Empty string means no image for Force
    },
    {
      title: "Producers, Consumers, and Decomposers",
      subtitle1: "",
      standardNumber: "",
      description: "",
      video: "",
      music: "",
      lyrics: [],
      game: "",
      titleImage: "", // Empty string means no image for Force
    },
    {
      title: "Refraction",
      subtitle1: "",
      standardNumber: "",
      description: "",
      video: "",
      music: "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/music%2Frefraction2025song.mp3?alt=media&token=0ba3b678-a6e0-481c-aca3-44ab676f8fbb",
      lyrics: ["(chorus)",
"Refraction is the bending of light.",
"Refraction is the bending of light.",
"Refraction is the bending of light.",
"Refraction is the bending of light.",

"(verse)",
"When light hits the air it’s moving fast,",
"going on a straight path.",
"But what happens when it hits water?",
"Light slows down and bends on a new path.",
"Now, as light goes back into the air,",
"it speeds up and bends back again.",
"That’s refraction.",
"Refraction is when light bends.",

"(chorus)",
"Refraction is the bending of light.",
"Refraction is the bending of light.",
"Refraction is the bending of light.",
"Refraction is the bending of light.",

"© Smart Board Learning, LLC. All rights reserved.",],
      game: "",
      titleImage: "", // Empty string means no image for Force
    },
    {
      title: "Respiratory System",
      subtitle1: "",
      standardNumber: "",
      description: "",
      video: "",
      music: "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/music%2Frespiratorysystem2025song.mp3?alt=media&token=65825bea-6c19-466f-88d8-880a61c1fc35",
      lyrics: ["(Intro)",
"Breathe in, breathe out with your respiratory system.",
"Breathe in, breathe out with your respiratory system.",

"(Chorus)",
"(Breathe in—breathe in) Get oxygen.",
"(Breathe in—breathe in) Rib muscles contract.",
"(Breathe in—breathe in) Pull ribs up and out.",
"(Breathe in—breathe in) The diaphragm muscle contracts.",
"(Breathe out—breathe out) Get rid of gases.",
"(Breathe out—breathe out) Your body doesn’t need.",
"(Breathe out—breathe out) Rib muscles relax.",
"(Breathe out—breathe out) The diaphragm muscle relaxes.",

"(Verse 1)",
"Now oxygen enters through the mouth and the nose,",
"Then passes through the larynx and trachea.",
"The trachea is a tube that enters the chest cavity.",
"Breathe in—breathe out.",
"In the chest cavity, the trachea splits in two,",
"Smaller tubes called the bronchi.",
"Each bronchus then divides, forming the bronchial tube.",
"Oxygen goes straight to the lungs (to the lungs).",

"(Chorus)",
"(Breathe in—breathe in) Get oxygen.",
"(Breathe in—breathe in) Rib muscles contract.",
"(Breathe in—breathe in) Pull ribs up and out.",
"(Breathe in—breathe in) The diaphragm muscle contracts.",
"(Breathe out—breathe out) Get rid of gases.",
"(Breathe out—breathe out) Your body doesn’t need.",
"(Breathe out—breathe out) Rib muscles relax.",
"(Breathe out—breathe out) The diaphragm muscle relaxes.",

"(Verse 2)",
"The little sacs in the lungs are called alveoli.",
"The alveoli are surrounded by capillaries.",
"The capillaries diffuse the oxygen from the alveoli—alveoli (yeah).",
"At the same time, the veins release the carbon dioxide into the alveoli—into the alveoli.",
"When you breathe out, the carbon dioxide is leaving the lungs.",
"It's leaving the lungs.",

"© Smart Board Learning, LLC. All rights reserved.",],
      game: "",
      titleImage: "", // Empty string means no image for Force
    },
    {
      title: "Rocks & Minerals",
      subtitle1: "",
      standardNumber: "",
      description: "",
      video: "",
      music: "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/music%2Frocksandminerals2025song.mp3?alt=media&token=908ef6b0-b264-49ea-b2d4-742bc97ebed9",
      lyrics: ["(Chorus)",
"We use rocks & minerals every day.",
"We use rocks & minerals every day.",
"We use rocks & minerals every day.",
"We use rocks & minerals every day.",

"(Verse 1)",
"Buildings and bridges are all made of rock.",
"Even the roads we drive on with our car.",
"Metals come from minerals.",
"Cars and planes use different minerals.",
"The salt on my food is a mineral.",
"The gold that I buy is a mineral.",
"Every day, we’re all around rocks and minerals.",
"Now, can you name some uses of rocks and minerals?",

"(Chorus)",
"We use rocks & minerals every day.",
"We use rocks & minerals every day.",
"We use rocks & minerals every day.",
"We use rocks & minerals every day.",

"(Verse 2)",
"Now all rocks are made up of two or more minerals,",
"But minerals aren't made up of rock.",
"Now all rocks are made up of two or more minerals,",
"But minerals aren't made up of rock.",
"There's about 3,000 minerals known on Earth,",
"And different combinations of them make rocks.",

"(Chorus)",
"We use rocks & minerals every day.",
"We use rocks & minerals every day.",
"We use rocks & minerals every day.",
"We use rocks & minerals every day.",

"© Smart Board Learning, LLC. All rights reserved.",],
      game: "",
      titleImage: "", // Empty string means no image for Force
    },
    {
      title: "Seasons",
      subtitle1: "",
      standardNumber: "",
      description: "",
      video: "",
      music: "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/music%2Fseasons2025song.mp3?alt=media&token=04a34ce0-7b3c-4e70-8564-cc5ac75c2bfc",
      lyrics: ["(chorus)",
"There is something we have to get clear now,",
"there are four seasons in every year now.",
"Do you know the reasons for the seasons? Check this,",
"seasons come because of the tilt of the Earth's axis.",

"(verse)",
"We spring ahead in the Spring, then comes the Summer.",
"Watch the leaves change color, we fall back in Autumn.",
"Then comes the Winter, which is the coldest season.",
"The Earth spins on an axis that is tilted, is the reason why this happens.",

"(chorus)",
"There is something we have to get clear now,",
"there are four seasons in every year now.",
"Do you know the reasons for the seasons? Check this,",
"seasons come because of the tilt of the Earth's axis.",

"(bridge)",
"Summer, Winter, Spring, Fall.",
"Summer, Winter, Spring, Fall.",
"Summer, Winter, Spring, Fall.",
"Summer, Winter, Spring, Fall.",

"© Smart Board Learning, LLC. All rights reserved.",],
      game: "",
      titleImage: "", // Empty string means no image for Force
    },
    {
      title: "Soil, Soil, What Can You Do?",
      subtitle1: "",
      standardNumber: "",
      description: "",
      video: "",
      music: "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/music%2Fsoilsoilwhatcanyoudo2025song.mp3?alt=media&token=91c5d957-f41c-44f2-870a-c0f6eb120d00",
      lyrics: ["(Chorus)",
"Soil, soil, what can you do?",
"Soil, soil, it’s all up to you.",
"Helping plants live, helping plants grow.",
"Soil, soil, it’s all up to you.",

"(Verse 1)",
"Sandy soil is loose and light,",
"Water slips right through because sandy soil don’t hold tight.",
"It doesn’t hold much water, it’s dry and bare,",
"But mix it up, and plants grow there!",
"Clay is heavy, sticky, and slow,",
"It holds the water, it doesn’t let it go.",
"It’s harder for plants to grow in clay because roots need air,",
"But mix it with sand, and plants grow there!",
"Loam is perfect, the best of all,",
"It holds the water and lets roots crawl.",
"With nutrients rich and texture just right,",
"Plants grow strong, reaching new heights!",

"(Chorus)",
"Soil, soil, what can you do?",
"Help the plants grow, it’s up to you!",
"Some hold water, some stay dry,",
"Soil helps the roots reach up to the sky!",

"(Bridge)",
"Each soil is different, each one can bring,",
"Water, air, and nutrients for everything.",
"Soil supports the plants, helps them survive,",
"With the right soil, the plants will thrive!",
"So let’s learn about soil, it’s fun to know,",
"What helps our plants and gardens grow.",
"From sandy to clay, and loam so true,",
"Soil, soil, we depend on you!",

"(Chorus)",
"Soil, soil, what can you do?",
"Help the plants grow, it’s up to you!",
"Some hold water, some stay dry,",
"Soil helps the roots reach up to the sky!",

"© Smart Board Learning, LLC. All rights reserved.",],
      game: "",
      titleImage: "", // Empty string means no image for Force
    },
    {
      title: "Spectrum",
      subtitle1: "",
      standardNumber: "",
      description: "",
      video: "",
      music: "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/music%2Fspectrum2025song.mp3?alt=media&token=6bf51fc3-840a-44c9-b6f7-011bdbce70c7",
      lyrics: ["(chorus)",
"The visible spectrum is made of colors,",
"The visible spectrum is made of colors.",
"Red, orange, yellow, green, blue, indigo, violet.",
"Red, orange, yellow, green, blue, indigo, violet.",
"The visible spectrum is seen as light.",
"The visible spectrum is seen as light.",
"Each color has a different a wavelength in the spectrum.",
"Each color has a different a wavelength in the spectrum.",
"Red has the longest wavelength.",
"Red has the longest wavelength.",
"Red has the longest wavelength.",
"Red has the longest wavelength.",
"Violet has the shortest wavelength.",
"Violet has the shortest wavelength.",
"Violet has the shortest wavelength in the spectrum,",
"in the spectrum.",

"© Smart Board Learning, LLC. All rights reserved.",],
      game: "",
      titleImage: "", // Empty string means no image for Force
    },
    {
      title: "Title",
      subtitle1: "",
      standardNumber: "",
      description: "",
      video: "",
      music: "",
      lyrics: [],
      game: "",
      titleImage: "", // Empty string means no image for Force
    },
    {
      title: "Title",
      subtitle1: "",
      standardNumber: "",
      description: "",
      video: "",
      music: "",
      lyrics: [],
      game: "",
      titleImage: "", // Empty string means no image for Force
    },
    {
      title: "Title",
      subtitle1: "",
      standardNumber: "",
      description: "",
      video: "",
      music: "",
      lyrics: [],
      game: "",
      titleImage: "", // Empty string means no image for Force
    },
    {
      title: "Title",
      subtitle1: "",
      standardNumber: "",
      description: "",
      video: "",
      music: "",
      lyrics: [],
      game: "",
      titleImage: "", // Empty string means no image for Force
    },
    {
      title: "Title",
      subtitle1: "",
      standardNumber: "",
      description: "",
      video: "",
      music: "",
      lyrics: [],
      game: "",
      titleImage: "", // Empty string means no image for Force
    },
    {
      title: "Title",
      subtitle1: "",
      standardNumber: "",
      description: "",
      video: "",
      music: "",
      lyrics: [],
      game: "",
      titleImage: "", // Empty string means no image for Force
    },
    {
      title: "Title",
      subtitle1: "",
      standardNumber: "",
      description: "",
      video: "",
      music: "",
      lyrics: [],
      game: "",
      titleImage: "", // Empty string means no image for Force
    },
    {
      title: "Title",
      subtitle1: "",
      standardNumber: "",
      description: "",
      video: "",
      music: "",
      lyrics: [],
      game: "",
      titleImage: "", // Empty string means no image for Force
    },
    {
      title: "Title",
      subtitle1: "",
      standardNumber: "",
      description: "",
      video: "",
      music: "",
      lyrics: [],
      game: "",
      titleImage: "", // Empty string means no image for Force
    },
    {
      title: "Title",
      subtitle1: "",
      standardNumber: "",
      description: "",
      video: "",
      music: "",
      lyrics: [],
      game: "",
      titleImage: "", // Empty string means no image for Force
    },
    {
      title: "Title",
      subtitle1: "",
      standardNumber: "",
      description: "",
      video: "",
      music: "",
      lyrics: [],
      game: "",
      titleImage: "", // Empty string means no image for Force
    },
    {
      title: "Title",
      subtitle1: "",
      standardNumber: "",
      description: "",
      video: "",
      music: "",
      lyrics: [],
      game: "",
      titleImage: "", // Empty string means no image for Force
    },
    {
      title: "Title",
      subtitle1: "",
      standardNumber: "",
      description: "",
      video: "",
      music: "",
      lyrics: [],
      game: "",
      titleImage: "", // Empty string means no image for Force
    },
    {
      title: "Title",
      subtitle1: "",
      standardNumber: "",
      description: "",
      video: "",
      music: "",
      lyrics: [],
      game: "",
      titleImage: "", // Empty string means no image for Force
    },
    {
      title: "Title",
      subtitle1: "",
      standardNumber: "",
      description: "",
      video: "",
      music: "",
      lyrics: [],
      game: "",
      titleImage: "", // Empty string means no image for Force
    },
    {
      title: "Title",
      subtitle1: "",
      standardNumber: "",
      description: "",
      video: "",
      music: "",
      lyrics: [],
      game: "",
      titleImage: "", // Empty string means no image for Force
    },
    {
      title: "Title",
      subtitle1: "",
      standardNumber: "",
      description: "",
      video: "",
      music: "",
      lyrics: [],
      game: "",
      titleImage: "", // Empty string means no image for Force
    },
    {
      title: "Title",
      subtitle1: "",
      standardNumber: "",
      description: "",
      video: "",
      music: "",
      lyrics: [],
      game: "",
      titleImage: "", // Empty string means no image for Force
    },
    {
      title: "Title",
      subtitle1: "",
      standardNumber: "",
      description: "",
      video: "",
      music: "",
      lyrics: [],
      game: "",
      titleImage: "", // Empty string means no image for Force
    },

  ];

  // Extract all unique standardNumbers from topics in order of appearance
  const uniqueStandardNumbers = Array.from(
    new Set(topics.map(topic => topic.standardNumber))
  );

  // Map each unique standardNumber to a color based on position
  const standardNumberColorMap = {};
  uniqueStandardNumbers.forEach((number, index) => {
    const colorIndex = index % styles.topicColors.length;
    standardNumberColorMap[number] = styles.topicColors[colorIndex];
  });

  return (
    <div
      style={{
        ...styles.container,
        backgroundImage: backgroundImage
          ? `url(${backgroundImage})`
          : undefined,
        background: backgroundImage
          ? undefined
          : "linear-gradient(to bottom, #6dd5fa, #2980b9)", // Attractive red to pink gradient
      }}
    >
      <img
        src="https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/images%2FSBLLogoBlackTM.png?alt=media&token=2f8fde38-b373-4367-9ade-a7b68bae3b6f"
        alt="Logo"
        style={styles.logo}
      />
      <div style={styles.navButtons}>
        <button
          onClick={handleHomeClick}
          style={styles.homeButton} // Purple color
        >
          Home
        </button>
        <button
          onClick={() => handleSubjectClick("science")}
          style={styles.scienceButton} // Blue color
        >
          Science
        </button>
        <button
          onClick={() => handleSubjectClick("socialstudies")}
          style={styles.socialStudiesButton} // Green color
        >
          Social Studies
        </button>
        <button
          onClick={() => handleSubjectClick("math")}
          style={styles.mathButton} // Reddish color
        >
          Math
        </button>
        <button
          onClick={() => handleSubjectClick("other")}
          style={styles.otherButton} // Yellowish color
        >
          Other
        </button>
      </div>
      <h1 style={styles.title}>
  {user && user.firstName && user.lastName ? (
    <>
      All Science
      <br />
      {user.sirName} {user.lastName}'s Class
    </>
  ) : (
    "All Science Portal"
  )}
</h1>

      <p style={styles.subtitle}></p>
      <p style={styles.subtitle}>Watch the video or listen to the song to explore new concepts,<br></br> then play the game to reinforce learning in a fun way!<br></br><br></br> Educators can request custom materials, adjust content settings,<br></br> and track student progress below.</p> 
      
     {/* Topics */}
     <div style={styles.topicsContainer}>
     {topics.map((topic, index) =>
  visibleTopics[index] && topic.title?.trim() ? (
            <div key={index} style={{ 
              ...styles.topicCard, 
              borderColor: standardNumberColorMap[topic.standardNumber] || "#ccc" 
            }}>
              <div style={styles.topicContent}>
              {topic.titleImage ? (
  <>
    <img
      src={topic.titleImage}
      alt={topic.title}
      style={styles.titleImage}
    />
    <h3 style={styles.topicTitle}>{topic.title}</h3>
  </>
) : (
  <h3 style={styles.topicTitle}>{topic.title}</h3>
)}

               {topic.subtitle1 && <p style={styles.subtitle1}>{topic.subtitle1}</p>}
                <p style={styles.subtitle2}>Topic: {index + 1}</p>   
                <p style={styles.topicDescription}>
  <span style={styles.standardNumber}>{topic.standardNumber}</span> {topic.description}
</p>

              </div>
              <div style={styles.topicButtons}>
              {topic.video && (
  <button
    style={styles.button}
    onClick={() =>
      handleOpenPopup(
        <div style={styles.mediaStage}>
          <video
            src={topic.video}
            controls
            style={styles.media}
            controlsList="nodownload noplaybackrate noremoteplayback"
            disablePictureInPicture
            onContextMenu={(e) => e.preventDefault()}
          />
        </div>
      )
    }
  >
    <img
      src="https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/images%2Fvideo-topic-image-button2.png?alt=media&token=c787b8ef-9cfa-4051-ae09-9d58cee36987" // Example icon
      alt="Video"
      style={{ width: "20px", height: "20px", marginRight: "8px" }}
    />
    Video
  </button>
)}
{topic.music && (
  <button
    style={styles.button}
    onClick={() =>
      handleOpenPopup(
        <div>
          <audio
            src={topic.music}
            controls
            style={styles.mediaAudio}
            controlsList="nodownload noplaybackrate noremoteplayback"
            disablePictureInPicture
            onContextMenu={(e) => e.preventDefault()}
          />
          <div style={styles.lyricsContainer}>
            {topic.lyrics.map((line, idx) => (
              <p key={idx} style={styles.lyricLine}>
                {line}
              </p>
            ))}
          </div>
        </div>
      )
    }
  >
    🎵 Music
  </button>
)}
{topic.game && (
  <button style={styles.button} onClick={() => handleGameClick(topic.game)}>
    🎮 Game
  </button>
)}

              </div>
              <div style={{ marginTop: "15px", textAlign: "left" }}>
        {[
          "Images for Video",
          "Lyrics in Video",
          "Video Completed",
          "Lyrics for Song",
          "Song Completed",
          "Images for Game",
          "Questions for Game",
          "Game Completed"
        ].map((label, idx) => {
          const key = label.replace(/\s+/g, '');
          const topicKey = topic.title;
          const isChecked = finishedItems[topicKey]?.[key]?.completed || false;

          return (
            <div key={idx}>
              <label>
                <input
                  type="checkbox"
                  checked={isChecked}
                  onChange={() => handleCheckboxConfirm(index, key)}
                />
                {label}
              </label>
            </div>
          );
        })}
      </div>
            </div>
          ) : null
        )}
      </div>

      {popupContent && (
        <div style={styles.popupOverlay}>
          <div style={styles.popupBox}>
            <button onClick={handleClosePopup} style={styles.closeButton}>
              ✖
            </button>
            {popupContent}
          </div>
        </div>
      )}
      
      {/* Educator Controls Container */}
      {educatorControlsVisible && (
        <div style={styles.popupOverlay}>
          <div style={styles.popupBox}>
            <button onClick={handleEducatorControlsClose} style={styles.closeButton}>
              ✖
            </button>
            <div style={styles.educatorControls}>
              <h2>Educator Controls</h2>
              <h3>Check all tools you want your student to see. Uncheck those you want to hide.</h3>
              <div style={styles.controlsGrid}>
                {topics.map((topic, index) => (
                  <div key={index} style={styles.controlItem}>
                    <label>
                      <input
                        type="checkbox"
                        checked={visibleTopics[index]}
                        onChange={() => handleCheckboxChange(index)}
                      />
                      {topic.title}
                    </label>
                  </div>
                ))}
              </div>
              <button onClick={handleSaveChanges} style={styles.saveButton}>
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Buttons Container */}
      <div style={styles.buttonsContainer}>
        <button style={styles.educatorControlsButton} onClick={handleEducatorControlsClick}>
          Click Here for Educator Controls
        </button>
        <button style={styles.requestButton} onClick={handleRequestClick}>
          Click Here to Request Custom Material
        </button>
        <button style={styles.studentProgressButton} onClick={handleStudentProgressClick}>
          Click Here for Student Progress
        </button>
      </div>

      {/* Form Container */}
      {formVisible && (
        <div style={styles.popupOverlay}>
          <div style={styles.popupBox}>
            <button onClick={handleFormClose} style={styles.closeButton}>
              ✖
            </button>
            <div style={styles.formContainer}>
              <h2>Custom Material Request</h2>
              <form>
                <div style={styles.formRow}>
                  <div style={styles.formField}>
                    <label>Sir Name:</label>
                    <select style={styles.input}>
                      <option>Mr.</option>
                      <option>Mrs.</option>
                      <option>Ms.</option>
                      <option>Miss</option>
                      <option>N/A</option>
                    </select>
                  </div>
                  <div style={styles.formField}>
                    <label>First Name:</label>
                    <input type="text" style={styles.input} />
                  </div>
                  <div style={styles.formField}>
                    <label>Last Name:</label>
                    <input type="text" style={styles.input} />
                  </div>
                  <div style={styles.formField}>
                    <label>Grade:</label>
                    <select style={styles.input}>
                      <option>1st</option>
                      <option>2nd</option>
                      <option>3rd</option>
                      <option>4th</option>
                      <option>5th</option>
                    </select>
                  </div>
                  <div style={styles.formField}>
                    <label>State:</label>
                    <select style={styles.input}>
                      <option>AL</option>
                      <option>AK</option>
                      <option>AZ</option>
                      <option>AR</option>
                      <option>CA</option>
                      <option>CO</option>
                      <option>CT</option>
                      <option>DE</option>
                      <option>DC</option>
                      <option>FL</option>
                      <option>GA</option>
                      <option>HI</option>
                      <option>ID</option>
                      <option>IL</option>
                      <option>IN</option>
                      <option>IA</option>
                      <option>KS</option>
                      <option>KY</option>
                      <option>LA</option>
                      <option>ME</option>
                      <option>MD</option>
                      <option>MA</option>
                      <option>MI</option>
                      <option>MN</option>
                      <option>MS</option>
                      <option>MO</option>
                      <option>MT</option>
                      <option>NE</option>
                      <option>NV</option>
                      <option>NH</option>
                      <option>NJ</option>
                      <option>NM</option>
                      <option>NY</option>
                      <option>NC</option>
                      <option>ND</option>
                      <option>OH</option>
                      <option>OK</option>
                      <option>OR</option>
                      <option>PA</option>
                      <option>RI</option>
                      <option>SC</option>
                      <option>SD</option>
                      <option>TN</option>
                      <option>TX</option>
                      <option>UT</option>
                      <option>VT</option>
                      <option>VA</option>
                      <option>WA</option>
                      <option>WV</option>
                      <option>WI</option>
                      <option>WY</option>
                    </select>
                  </div>
                  <div style={styles.formField}>
                    <label>Subject:</label>
                    <select style={styles.input}>
                      <option>Math</option>
                      <option>Science</option>
                      <option>Social Studies</option>
                      <option>Other</option>
                    </select>
                  </div>
                  <div style={styles.formField}>
                    <label>Style:</label>
                    <select style={styles.input}>
                      <option>Select Style</option>
                      <option>Light Rock</option>
                      <option>Hip Hop</option>
                      <option>Country</option>
                      <option>Latin</option>
                      <option>R&B</option>
                      <option>Any Style</option>
                    </select>
                  </div>
                </div>
                <p></p>
                <p></p>
                <div style={styles.formField}>
                  <label>Information / Facts:</label>
                  <textarea placeholder="Please provide all facts needed in bullets for new material" style={styles.textarea}></textarea>
                </div>
                <p></p>
                <div style={styles.formField}>
                  <label>Game Questions & Answers <br></br>(All questions begin with Q-, all answers begin with A-. <br></br>The multiple choice correct answer begins with *.):</label>
                  <textarea placeholder="Please provide all games questions and answer." style={styles.textarea}></textarea>
                </div>
                <p>
                  Please allow 7-14 days for the material to be added to your portal. All custom material is protected under the Terms of Use. Payment for custom material allows the user to use the custom material within their portal until canceled.
                </p>
                <div>
                  <button type="button" onClick={() => window.open("https://example.com/pay", "_blank")} style={styles.payButton}>
                    Pay Now
                  </button>
                  <button type="button" onClick={handleFormClose} style={styles.cancelButton}>
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      <footer style={styles.footer}>
        <p>Smart Board Learning & Freinds</p>
        <p>Trademark and copyright © 2018-2025</p>
      </footer>
    </div>
  );
};

const baseNavButton = {
  color: "#fff",
  padding: "10px 20px",
  fontSize: "25px",
  border: "none",
  borderRadius: "5px",
  cursor: "pointer",
  width: "200px",
  height: "200px",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  textAlign: "center",
  boxShadow: "0 6px 0 rgba(0, 0, 0, 0.4), 0 8px 10px rgba(0, 0, 0, 0.4)", // Layered shadow
  border: "2px solid #222", // Border for depth
  transition: "transform 0.2s, box-shadow 0.2s",
};

const styles = {
  container: {
    textAlign: "center",
    fontFamily: "Arial, sans-serif",
    minHeight: "100vh", // Ensure the background covers the full viewport height
    padding: "20px",
    backgroundSize: "cover", // Ensure the background image or gradient covers the entire container
    backgroundPosition: "center",
    backgroundColor: "#f0f4f8", // Softer background color
    background: "radial-gradient(circle at 30% 30%, #d0eaff, #2980b9)", // Updated gradient
  },
  footer: {
    marginTop: "40px",
    fontSize: "20px",
    color: "#000",
    textAlign: "center",
  },
  logo: {
    width: "150px",
    margin: "0 auto",
    display: "block",
    transform: "rotate(-2deg)", // Slight tilt
    filter: "drop-shadow(2px 4px 2px rgba(0,0,0,0.4))", // Drop shadow for bounce effect
  },
  navButtons: {
    display: "flex",
    justifyContent: "center",
    marginTop: "20px",
    gap: "10px",
  },
  navButton: baseNavButton, // Base style for nav buttons
  homeButton: {
    ...baseNavButton,
    backgroundColor: "#A020F0", // Purple color
  },
  scienceButton: {
    ...baseNavButton,
    backgroundColor: "#4a90e2", // Blue color
  },
  socialStudiesButton: {
    ...baseNavButton,
    backgroundColor: "#4CAF50", // Green color
  },
  mathButton: {
    ...baseNavButton,
    backgroundColor: "#FF6347", // Reddish color
  },
  otherButton: {
    ...baseNavButton,
    backgroundColor: "#FFD700", // Yellowish color
  },
  title: {
    fontSize: "35px",
    color: "#000", // Change color to black
    marginTop: "20px",
  },
  subtitle: {
    fontSize: "20px",
    color: "#555", // Softer subtitle color
    marginBottom: "20px",
  },
  topicsContainer: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
    gap: "20px",
    padding: "20px",
  },
  topicCard: {
    backgroundColor: "rgba(255, 255, 255, 0.3)", // Semi-transparent white background
    borderRadius: "10px",
    boxShadow: "0 6px 10px rgba(0, 0, 0, 0.3), 0 2px 4px rgba(0, 0, 0, 0.2)", // Layered shadow for depth
    padding: "20px",
    textAlign: "center",
    backgroundImage: `
      linear-gradient(rgba(255, 255, 255, 0.8), rgba(255, 255, 255, 0.8)), 
      url('https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/images%2Fscience-bg-texture.png?alt=media&token=432e3d79-8a4d-4cde-9c13-7896c3a71f1b')
    `, // Add faded overlay
    backgroundSize: "cover",
    backgroundBlendMode: "overlay", // Blend the gradient and image
    border: "5px solid", // Add border
  },
  topicTitle: {
    fontSize: "22px",
    color: "#333",
    marginBottom: "10px",
    fontFamily: "'Quicksand', sans-serif", // Playful font without cursive
  },
  topicContent: {
    marginBottom: "20px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
  },
  titleImage: {
    width: "100%",
    maxHeight: "150px",
    objectFit: "contain",
    marginBottom: "10px",
  },
  subtitle1: {
    fontSize: "18px",
    color: "#777", // Softer subtitle color
    fontStyle: "italic",
  },
  subtitle2: {
    fontSize: "14px",
    color: "#555",
  },
  topicDescription: {
    fontSize: "14px",
    color: "#555", // Softer description color
    textAlign: "center",
  },
  topicButtons: {
    display: "flex",
    justifyContent: "center",
    gap: "10px",
  },
  button: {
    backgroundColor: "#4a90e2", // Softer blue color
    color: "#fff",
    padding: "10px",
    fontSize: "14px",
    border: "none",
    borderRadius: "5px",
    cursor: "pointer",
    flex: "1",
    margin: "0 5px",
    boxShadow: "0 4px 0 #1e5aa8, 0 6px 10px rgba(0,0,0,0.25)", // Blue shadow
    border: "2px solid #1e5aa8", // Blue border
    transition: "transform 0.2s, box-shadow 0.2s",
  },
  ":hover": {
    transform: "translateY(-2px)",
    boxShadow: "0 8px 12px rgba(0, 0, 0, 0.4), 0 4px 6px rgba(0, 0, 0, 0.2)", // Enhanced shadow on hover
  },
  ":active": {
    transform: "translateY(0)",
    boxShadow: "0 4px 6px rgba(0, 0, 0, 0.4), 0 2px 4px rgba(0, 0, 0, 0.2)", // Subtle shadow on click
  },
  popupOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 1000,
  },
  popupBox: {
    // Without this a long lyric sheet pushes the box off-screen. Content
    // scrolls inside the popup now; the popup itself never exceeds the
    // viewport.
    maxHeight: "90vh",
    overflowY: "auto",
    backgroundColor: "#fff",
    padding: "20px",
    borderRadius: "10px",
    boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)", // Softer shadow
    position: "relative",
    maxWidth: "600px",
    width: "90%",
  },
  closeButton: {
    position: "absolute",
    top: "10px",
    right: "10px",
    backgroundColor: "#ff4d4d",
    color: "#fff",
    border: "none",
    borderRadius: "50%",
    width: "30px",
    height: "30px",
    fontSize: "18px",
    cursor: "pointer",
    textAlign: "center",
    lineHeight: "30px",
  },
  // A fixed 16:9 stage. Every video renders in an identically sized frame
  // regardless of its source dimensions — a portrait clip gets black bars on
  // the sides instead of stretching the popup taller than the screen, which
  // is what forced people to scroll.
  mediaStage: {
    width: "100%",
    aspectRatio: "16 / 9",
    background: "#000",
    borderRadius: "8px",
    overflow: "hidden",
    marginTop: "10px",
  },
  media: {
    width: "100%",
    height: "100%",
    objectFit: "contain",
    display: "block",
    border: "none",
    background: "#000",
  },
  // Audio has no intrinsic height, so it keeps a simple full-width rule.
  mediaAudio: {
    width: "100%",
    marginTop: "10px",
  },
  lyricsContainer: {
    marginTop: "20px",
    textAlign: "center",
    maxHeight: "500px", // Set a maximum height
    overflowY: "auto", // Enable vertical scrolling
  },
  lyricLine: {
    fontSize: "16px",
    color: "#333",
    margin: "5px 0",
  },
  educatorControls: {
    backgroundColor: "#e0e7ef", // Softer background color for educator controls
    padding: "20px",
    borderRadius: "10px",
    marginTop: "20px",
  },
  controlsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(5, 1fr)",
    gap: "10px",
  },
  controlItem: {
    textAlign: "left",
  },
  formContainer: {
    backgroundColor: "#f9f9f9",
    padding: "30px",
    borderRadius: "10px",
    marginTop: "20px",
    boxShadow: "0 4px 8px rgba(0, 0, 0, 2)",
  },
  formRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: "30px",
  },
  formField: {
    flex: "1 1 200",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  input: {
    width: "80%",
    padding: "10px",
    borderRadius: "5px",
    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
    border: "1px solid #ccc",
    marginTop: "5px",
  },
  textarea: {
    width: "80%",
    padding: "10",
    borderRadius: "5px",
    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
    border: "1px solid #ccc",
    marginTop: "10px",
    minHeight: "100px",
  },
  payButton: {
    backgroundColor: "#4a90e2", // Softer blue color
    color: "#fff",
    padding: "10px 20px",
    fontSize: "16px",
    border: "none",
    borderRadius: "5px",
    cursor: "pointer",
    boxShadow: "0 2px 4px rgba(0, 0, 0, 2)",
    marginTop: "20px",
  },
  requestButton: {
    backgroundColor: "#ffa500", // Orange color
    color: "#fff",
    width: "200px",
    height: "200px",
    fontSize: "20px",
    fontWeight: "bold",
    border: "none",
    borderRadius: "5px",
    cursor: "pointer",
    boxShadow: "0 6px 0 #cc7000, 0 8px 10px rgba(0,0,0,0.4)", // Layered shadow
    border: "2px solid #b86e00", // Border for depth
    marginTop: "20px",
    textAlign: "center",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  cancelButton: {
    backgroundColor: "#ff4d4d",
    color: "#fff",
    padding: "10px 20px",
    fontSize: "16px",
    border: "none",
    borderRadius: "5px",
    cursor: "pointer",
    boxShadow: "0 2px 4px rgba(0, 0, 0, 2)",
    marginTop: "20px",
    marginLeft: "10px",
  },
  educatorControlsButton: {
    backgroundColor: "#A020F0", // Purple color
    color: "#fff",
    width: "200px",
    height: "200px",
    fontSize: "20px",
    fontWeight: "bold",
    border: "none",
    borderRadius: "5px",
    cursor: "pointer",
    boxShadow: "0 6px 0 #5e1280, 0 8px 10px rgba(0,0,0,0.4)", // Layered shadow
    border: "2px solid #4a0e60", // Border for depth
    marginTop: "20px",
    marginLeft: "10px",
    textAlign: "center",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  buttonsContainer: {
    display: "flex",
    justifyContent: "center",
    gap: "20px",
    marginTop: "20px",
  },
  studentProgressButton: {
    backgroundColor: "#ff69b4", // Pink color
    color: "#fff",
    width: "200px",
    height: "200px",
    fontSize: "20px",
    fontWeight: "bold",
    border: "none",
    borderRadius: "5px",
    cursor: "pointer",
    boxShadow: "0 6px 0 #b4004b, 0 8px 10px rgba(0,0,0,0.4)", // Layered shadow
    border: "2px solid #8e0038", // Border for depth
    textAlign: "center",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    marginTop: "20px", // Add marginTop to align the button
  },
  studentProgress: {
    backgroundColor: "#f9f9f9",
    padding: "20px",
    borderRadius: "10px",
    marginTop: "20px",
    boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
  },
  studentButtons: {
    display: "flex",
    flexWrap: "wrap",
    gap: "10px",
    justifyContent: "center",
  },
  studentButton: {
    backgroundColor: "#4a90e2", // Softer blue color
    color: "#000",
    padding: "10px 20px",
    fontSize: "16px",
    border: "none",
    borderRadius: "5px",
    cursor: "pointer",
    boxShadow: "0 2px 4px rgba(0, 0, 0, 2)",
  },
  saveButton: {
    backgroundColor: "linear-gradient(to bottom, #4caf50, #388e3c)", // Match button style
    color: "#fff",
    padding: "10px 20px",
    fontSize: "16px",
    border: "none",
    borderRadius: "5px",
    cursor: "pointer",
    boxShadow: "0 4px 0 #2e7d32, 0 6px 10px rgba(0,0,0,0.25)", // Layered shadow
    border: "2px solid #1b5e20", // Border for depth
    marginTop: "20px",
  },
  standardNumber: {
    fontWeight: "bold",
    color: "#222",
    marginRight: "5px",
  },
  topicColors: [
    "#FF6347",  // Reddish
    "#4CAF50",  // Green
    "#FFD700",  // Yellow
    "#4a90e2",  // Blue
    "#A020F0",  // Purple
    "#FF69B4",  // Pink
    "#FFA500",  // Orange
    "#2980b9",  // Deep Blue
    "#00CED1",  // Dark Turquoise (new)
    "#8B4513",  // Saddle Brown (new)
  ],
};

export default AllEducatorScience;
